// Library
import parseMerchHtml, { BANDCAMP_BASE } from '@/library/parseMerchHtml';

const CACHE_KEY = 'funus-merch-cache-v1';
const CACHE_TTL = 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT = 5000;

const PROXIES = [
    (url) => `https://cors.eu.org/${url}`,
    (url) => `https://proxy.cors.sh/${url}`,
    (url) => `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
    (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
];

const fetchThroughProxy = (buildUrl, signal) => fetch(buildUrl(`${BANDCAMP_BASE}/merch`), { signal })
    .then(async (response) => {
        if (!response.ok) {
            throw new Error(`Proxy responded ${response.status}`);
        }

        const merch = parseMerchHtml(await response.text());

        if (!merch.length) {
            throw new Error('No merch parsed');
        }

        return merch;
    });

const firstSuccess = (promises) => new Promise((resolve, reject) => {
    let pending = promises.length;

    promises.forEach((promise) => {
        promise.then(resolve).catch(() => {
            pending -= 1;

            if (pending === 0) {
                reject(new Error('All proxies failed'));
            }
        });
    });
});

export const readMerchCache = () => {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);

        if (!raw) {
            return null;
        }

        const cached = JSON.parse(raw);

        if (!cached.items || !cached.items.length) {
            return null;
        }

        return cached;
    } catch (error) {
        return null;
    }
};

export const writeMerchCache = (merch) => {
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({ items: merch, cachedAt: Date.now() }));
    } catch (error) {
        // ignore quota / unavailable storage
    }
};

export const clearMerchCache = () => {
    try {
        window.localStorage.removeItem(CACHE_KEY);
    } catch (error) {
        // ignore unavailable storage
    }
};

export const isMerchCacheStale = (cached) => (Date.now() - cached.cachedAt) > CACHE_TTL;

export const fetchMerch = (signal) => Promise.race([
    firstSuccess(PROXIES.map((buildUrl) => fetchThroughProxy(buildUrl, signal))),
    new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error('Timed out')), FETCH_TIMEOUT);
    }),
]);

export const warmMerchCache = () => {
    const cached = readMerchCache();

    if (cached && !isMerchCacheStale(cached)) {
        return Promise.resolve(cached.items);
    }

    const controller = new AbortController();

    return fetchMerch(controller.signal)
        .then((merch) => {
            writeMerchCache(merch);

            return merch;
        })
        .catch(() => null)
        .finally(() => controller.abort());
};
